/**
 * VIP授权码API服务
 * 参考vipApi.ts实现，支持API签名
 */

import { signRequest } from '../utils/apiSignature.js';
import { useAuthStore } from '../stores/auth.js';

/**
 * VIP授权码状态
 */
export type VipCodeStatus = 'unused' | 'used' | 'disabled';

/**
 * VIP授权码模型
 */
export interface VipActivationCode {
  id: string;
  code: string;
  name: string;
  vip_level: number;
  duration_months: number;
  status: VipCodeStatus;
  used_by: string | null;
  used_at: string | null;
  created_by: string;
  created_at: string;
  batch_id: string | null;
  remark: string | null;
}

/**
 * 授权码统计
 */
export interface VipCodeStats {
  total: number;
  unused_count: number;
  used_count: number;
  disabled_count: number;
  vip_count: number;
  vvip_count: number;
}

/**
 * 批次信息
 */
export interface VipCodeBatch {
  batch_id: string;
  name: string;
  vip_level: number;
  duration_months: number;
  count: number;
  unused_count: number;
  used_count: number;
  created_at: string;
}

/**
 * API响应格式
 */
interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

/**
 * VIP授权码API服务类
 */
class VipCodeApiService {
  private baseUrl: string;

  constructor() {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;
    this.baseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }

  /**
   * 通用请求方法（带签名）
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    requireSignature = false
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const method = options.method || 'GET';

    // 从 authStore 获取 token
    const token = useAuthStore.getState().accessToken;

    // 解析请求体
    let body: unknown = undefined;
    if (options.body) {
      try {
        body = JSON.parse(options.body as string);
      } catch {
        body = options.body;
      }
    }

    // 构建请求头
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string>),
    };

    // 如果需要签名，添加签名头
    if (requireSignature) {
      const signatureHeaders = await signRequest(url, method, body as Record<string, unknown> | undefined, {});
      Object.assign(headers, signatureHeaders);
    }

    const defaultOptions: RequestInit = {
      ...options,
      headers,
    };

    const response = await fetch(url, defaultOptions);

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData: ApiResponse = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // 如果无法解析错误响应，使用默认错误消息
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  // ============================================
  // 管理员API
  // ============================================

  /**
   * 获取授权码统计
   */
  async getVipCodeStats(): Promise<ApiResponse<{ stats: VipCodeStats }>> {
    return this.request('/api/admin/vip-codes/stats');
  }

  /**
   * 获取批次列表
   */
  async getVipCodeBatches(): Promise<ApiResponse<{ batches: VipCodeBatch[] }>> {
    return this.request('/api/admin/vip-codes/batches');
  }

  /**
   * 生成授权码
   */
  async generateVipCodes(data: {
    name: string;
    vip_level: number;
    duration_months: number;
    count: number;
    remark?: string;
  }): Promise<ApiResponse<{ codes: VipActivationCode[]; count: number }>> {
    return this.request('/api/admin/vip-codes/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * 获取授权码列表
   */
  async getVipCodeList(params: {
    page?: number;
    pageSize?: number;
    status?: VipCodeStatus;
    vipLevel?: number;
    batchId?: string;
    keyword?: string;
  }): Promise<
    ApiResponse<{
      data: VipActivationCode[];
      pagination: {
        page: number;
        page_size: number;
        total: number;
        total_pages: number;
      };
    }>
  > {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.vipLevel !== undefined) queryParams.append('vipLevel', params.vipLevel.toString());
    if (params.batchId) queryParams.append('batchId', params.batchId);
    if (params.keyword) queryParams.append('keyword', params.keyword);

    return this.request(`/api/admin/vip-codes?${queryParams}`);
  }

  /**
   * 禁用授权码
   */
  async disableVipCode(codeId: string): Promise<ApiResponse<{ code: VipActivationCode }>> {
    return this.request(`/api/admin/vip-codes/${codeId}/disable`, {
      method: 'PATCH',
    });
  }

  /**
   * 删除授权码
   */
  async deleteVipCode(codeId: string): Promise<ApiResponse> {
    return this.request(`/api/admin/vip-codes/${codeId}`, {
      method: 'DELETE',
    });
  }

  /**
   * 导出授权码
   */
  async exportVipCodes(params: {
    status?: VipCodeStatus;
    vipLevel?: number;
    batchId?: string;
  }): Promise<ApiResponse<{ codes: Record<string, string | number>[]; count: number }>> {
    return this.request('/api/admin/vip-codes/export', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // ============================================
  // 用户API
  // ============================================

  /**
   * 验证授权码
   */
  async verifyVipCode(code: string): Promise<
    ApiResponse<{
      code: string;
      name: string;
      vip_level: number;
      vip_level_name: string;
      duration_months: number;
    }>
  > {
    return this.request(`/api/vip/code/${encodeURIComponent(code)}/verify`);
  }

  /**
   * 激活授权码（带签名）
   */
  async activateVipCode(code: string): Promise<
    ApiResponse<{
      vip_level: number;
      vip_level_name: string;
      expire_at: string;
      duration_months: number;
      converted_months?: number;
    }>
  > {
    return this.request(
      '/api/vip/activate-code',
      {
        method: 'POST',
        body: JSON.stringify({ code }),
      },
      true // 需要签名
    );
  }
}

// 创建单例实例
const vipCodeApiServiceInstance = new VipCodeApiService();

// ============================================
// 导出函数（兼容旧API）
// ============================================

export async function getVipCodeStats() {
  return vipCodeApiServiceInstance.getVipCodeStats();
}

export async function getVipCodeBatches() {
  return vipCodeApiServiceInstance.getVipCodeBatches();
}

export async function generateVipCodes(data: {
  name: string;
  vip_level: number;
  duration_months: number;
  count: number;
  remark?: string;
}) {
  return vipCodeApiServiceInstance.generateVipCodes(data);
}

export async function getVipCodeList(params: {
  page?: number;
  pageSize?: number;
  status?: VipCodeStatus;
  vipLevel?: number;
  batchId?: string;
  keyword?: string;
}) {
  return vipCodeApiServiceInstance.getVipCodeList(params);
}

export async function disableVipCode(codeId: string) {
  return vipCodeApiServiceInstance.disableVipCode(codeId);
}

export async function deleteVipCode(codeId: string) {
  return vipCodeApiServiceInstance.deleteVipCode(codeId);
}

export async function exportVipCodes(params: {
  status?: VipCodeStatus;
  vipLevel?: number;
  batchId?: string;
}) {
  return vipCodeApiServiceInstance.exportVipCodes(params);
}

export async function verifyVipCode(code: string) {
  return vipCodeApiServiceInstance.verifyVipCode(code);
}

export async function activateVipCode(code: string) {
  return vipCodeApiServiceInstance.activateVipCode(code);
}

/**
 * VIP等级显示信息
 */
export const VIP_LEVEL_INFO = {
  1: { name: 'VIP会员', color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
  2: { name: 'VVIP会员', color: 'purple', bgColor: 'bg-purple-100', textColor: 'text-purple-700' },
} as const;

/**
 * 获取VIP等级显示信息
 */
export function getVipLevelDisplay(level: number) {
  return VIP_LEVEL_INFO[level as keyof typeof VIP_LEVEL_INFO] || VIP_LEVEL_INFO[1];
}

/**
 * 授权码状态显示信息
 */
export const CODE_STATUS_INFO = {
  unused: { name: '未使用', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  used: { name: '已使用', color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-700' },
  disabled: { name: '已禁用', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-700' },
} as const;

/**
 * 获取授权码状态显示信息
 */
export function getCodeStatusDisplay(status: VipCodeStatus) {
  return CODE_STATUS_INFO[status] || CODE_STATUS_INFO.unused;
}
